import { Like } from '@modules/common/types/like';
import { SortOrder } from '@modules/common/types/sort-order';
import { isNil } from '@modules/common/utils/base';
import { Priority } from '@sharedlib/common/types/tasking-app/priority';
import { Sorter } from '@sharedlib/common/lang/Sorter';
import { zonedDayIndex } from '@sharedlib/common/utils/timezone';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { isStitchBoundaryFloating } from '@modules/tasks/utils/stitch-time.util';

const PRIORITY_WEIGHT: Record<Priority, number> = {
  [Priority.High]: 3,
  [Priority.Normal]: 2,
  [Priority.Low]: 1,
};

/**
 * Does the task's START boundary fall inside the filter's `fromTime`/`toTime` range?
 *
 * The range is compared in the frame the boundary actually lives in (SBUX-829, reviewer finding #1):
 *
 * - A **timed** start is a real instant, so it is compared as one. Day-index comparison would widen an
 *   exact (non-day-aligned) range — `applyAdvancedFilters` and a user-picked `from`/`to` both produce
 *   those — to whole days.
 * - A **floating** start (all-day, or date-only) is a bare calendar date stored at UTC midnight. It
 *   denotes no instant, so an instant comparison asks a question it cannot answer: with the range in
 *   the account zone and the value at UTC midnight the two are up to a day apart, and a task shown as
 *   "Jul 30" vanished from a "Today = Jul 30" filter for every user west of UTC. Compared by CALENDAR
 *   DAY instead — the unit both sides really are — which is correct in every account/browser
 *   combination and rejects the neighbouring day (a floating Jul 31 no longer matches Today = Jul 30).
 *
 * Until this branch the DTO decoded an all-day value to a browser-local midnight carrier
 * (`@TransformAllDayDate`), which made the old browser-local comparison accidentally right for all-day
 * items — but never for date-only ones. Both are handled here.
 */
function matchesTimeRange(task: Task, filters: Like<TasksFilters>): boolean {
  if (!filters.fromTime && !filters.toTime) {
    return true;
  }
  if (!task.startDateTime) {
    // Pre-existing and timezone-independent: a task with only a DUE date has no start to range-check,
    // and is therefore hidden by ANY date filter. Left as-is deliberately — SBUX-829 is about frames,
    // and changing which tasks a filter is *about* is a product decision, not a timezone fix.
    return false;
  }

  if (!isStitchBoundaryFloating(task, 'start')) {
    return (
      (!filters.fromTime || task.startDateTime >= filters.fromTime) &&
      (!filters.toTime || task.startDateTime <= filters.toTime)
    );
  }

  // `zone` is the frame the RANGE is expressed in; the floating value itself is read in UTC, which is
  // where it is stored (see getStitchDateZone). A nullish zone falls back to the browser, preserving
  // today's behaviour for any surface that has not yet supplied one.
  const zone = filters.displayTimeZone;
  const day = zonedDayIndex(task.startDateTime, 'UTC');
  return (
    (!filters.fromTime || day >= zonedDayIndex(filters.fromTime, zone)) &&
    (!filters.toTime || day <= zonedDayIndex(filters.toTime, zone))
  );
}

function matchesFilters(task: Task, filters: Like<TasksFilters>): boolean {
  if (!isNil(filters.completed) && task.completed !== filters.completed) {
    return false;
  }
  if (!isNil(filters.flagged) && task.flagged !== filters.flagged) {
    return false;
  }
  if (!isNil(filters.pinned) && task.pinned !== filters.pinned) {
    return false;
  }
  if (!isNil(filters.snoozed) && !!task.snoozed !== filters.snoozed) {
    return false;
  }
  if (!isNil(filters.followed) && !!task.followed !== filters.followed) {
    return false;
  }
  if (filters.withPriority && (!task.priority || task.priority === Priority.Normal)) {
    return false;
  }
  if (filters.priority && task.priority !== filters.priority) {
    return false;
  }
  if (!matchesTimeRange(task, filters)) {
    return false;
  }

  const query = typeof filters.query === 'string' ? filters.query.trim().toLowerCase() : '';
  if (query && !(task.title || '').toLowerCase().includes(query)) {
    return false;
  }

  return true;
}

function buildComparator(filters: Like<TasksFilters>): (a: Task, b: Task) => number {
  const desc = filters.sortOrder === SortOrder.DESC;
  const directed = (sorter: Sorter<Task>): ((a: Task, b: Task) => number) =>
    (desc ? sorter.inverse() : sorter).build();

  switch (filters.sortBy) {
    case 'title':
      return directed(Sorter.byExtractor<Task>((t) => t.title ?? ''));
    case 'date':
      return directed(Sorter.byExtractor<Task>((t) => t.startDateTime?.getTime() ?? 0));
    case 'due-date':
      return directed(Sorter.byExtractor<Task>((t) => t.endDateTime?.getTime() ?? 0));
    case 'createdAt':
      return directed(Sorter.byExtractor<Task>((t) => t.createdAt?.getTime() ?? 0));
    case 'priority':
      // always high → low, independent of sort order
      return Sorter.byExtractor<Task>((t) => PRIORITY_WEIGHT[t.priority] ?? 0)
        .inverse()
        .build();
    case 'score':
      return directed(Sorter.byExtractor<Task>((t) => t.score ?? 0));
    case 'position':
    default:
      return directed(Sorter.byExtractor<Task>((t) => t.boardPosition ?? 0));
  }
}

// Lower rank bubbles to the top. Mirrors the legacy list's "on top" flags
// (pinned/flagged/snoozed/followed), which the backend applies for every sort except score.
// Precedence follows StitchFilters.format(). Follow-up differs from snooze: a followed task stays
// in its natural position WHILE PENDING and only bubbles to the top once its follow-up time has
// passed (followed <= now) — hence the expiry gate, which snooze does not need.
function onTopRank(task: Task, filters: Like<TasksFilters>): number {
  if (filters.pinnedOnTop && task.pinned) return 0;
  if (filters.flaggedOnTop && task.flagged) return 1;
  if (filters.snoozedOnTop && task.snoozed) return 2;
  if (filters.followedOnTop && task.followed && task.followed.getTime() <= Date.now()) return 3;
  return 4;
}

// Manual board ordering is the only mode where DnD reordering (which writes
// boardPosition) is meaningful; any other sort drives the visible order itself.
export function isManualOrder(filters: Like<TasksFilters>): boolean {
  return !filters?.sortBy || filters.sortBy === 'position';
}

export function applyTaskListFilters(tasks: Task[], filters?: Like<TasksFilters>): Task[] {
  if (!filters) {
    return tasks;
  }

  const survivors = tasks.filter((task) => matchesFilters(task, filters));
  const survivorIds = new Set(survivors.map(({ id }) => id));
  // drop subtasks whose parent was filtered out, otherwise they orphan to the root
  const visible = survivors.filter((task) => task.parentId === null || survivorIds.has(task.parentId));

  const topLevel = visible.filter((task) => task.parentId === null);
  const subtasks = visible.filter((task) => task.parentId !== null);

  const sortedTopLevel = [...topLevel].sort(buildComparator(filters));

  if (filters.sortBy !== 'score') {
    sortedTopLevel.sort((a, b) => onTopRank(a, filters) - onTopRank(b, filters));
  }

  return [...sortedTopLevel, ...subtasks];
}
